/** SSE consumer for `POST /api/v1/chat/browse` (TinyFish Agent). */

import { tokenStorage } from "@/auth/storage";
import { throwApiRequestError } from "@/api/client";

import { isBrowseWatchUrl } from "./browseWatch";
import { parseSseFrames } from "./sseClient";

export interface BrowseRequest {
  urls: string[];
  goal: string;
  prompt: string;
  conversationId?: string;
}

export type BrowseEvent =
  | { kind: "started"; run_id: string; host?: string }
  | { kind: "progress"; purpose: string; host?: string }
  | { kind: "watch"; url: string; host?: string }
  | { kind: "site_done"; host: string }
  | { kind: "complete"; markdown: string }
  | { kind: "error"; message: string };

export function parseBrowseEvent(
  event: string,
  data: Record<string, unknown>,
): BrowseEvent | null {
  if (event === "started" && typeof data.run_id === "string") {
    return {
      kind: "started",
      run_id: data.run_id,
      host: typeof data.host === "string" ? data.host : undefined,
    };
  }
  if (event === "progress") {
    return {
      kind: "progress",
      purpose: typeof data.purpose === "string" ? data.purpose : "",
      host: typeof data.host === "string" ? data.host : undefined,
    };
  }
  if (
    event === "watch" &&
    typeof data.url === "string" &&
    isBrowseWatchUrl(data.url)
  ) {
    return {
      kind: "watch",
      url: data.url,
      host: typeof data.host === "string" ? data.host : undefined,
    };
  }
  if (event === "site_done" && typeof data.host === "string") {
    return { kind: "site_done", host: data.host };
  }
  if (event === "complete" && typeof data.markdown === "string") {
    return { kind: "complete", markdown: data.markdown };
  }
  if (event === "error") {
    return {
      kind: "error",
      message: typeof data.message === "string" ? data.message : "Agent failed",
    };
  }
  return null;
}

export async function* streamBrowse(
  req: BrowseRequest,
  signal?: AbortSignal,
): AsyncGenerator<BrowseEvent, void, void> {
  const tokens = tokenStorage.load();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (tokens?.access_token) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }

  const res = await fetch("/api/v1/chat/browse", {
    method: "POST",
    headers,
    signal,
    body: JSON.stringify({
      urls: req.urls,
      url: req.urls[0],
      goal: req.goal,
      prompt: req.prompt,
      conversation_id: req.conversationId,
    }),
  });

  if (!res.ok) {
    await throwApiRequestError(res);
  }
  if (!res.body) {
    throw new Error("response had no body");
  }

  for await (const frame of parseSseFrames(res.body)) {
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(frame.data) as Record<string, unknown>;
    } catch {
      continue;
    }
    const ev = parseBrowseEvent(frame.event, data);
    if (!ev) continue;
    yield ev;
    if (ev.kind === "complete" || ev.kind === "error") return;
  }
}
